import { defineStore } from "pinia";
import axios from "@/utils/axios";
import { router } from "@/router";
import { getAuthHostName, getHostName } from "@/utils/urls";
import Swal from "sweetalert2";
import { useSharedStore } from "@/stores/shared-store";

export const useAuthStore = defineStore("Auth", {
  state: () => ({
    Url: getHostName(),
    role: "",
    authUrl: getAuthHostName(),
    sharedStore: useSharedStore(),
    currentUser: null,
    otherUsers: [],
    myaccess: [],
    verify_key: null,
  }),
  getters: {
    userName: (state) => {
      //
      return (id) => {
        const user = state.otherUsers.find((i) => i.id === id);
        if (user) {
          const words = String(user.nickname || "-").split(" ");
          return words.length > 2 ? words.slice(0, 2).join(" ") : user.nickname || "-";
        }
        return "-";
      };
    },
    userPhoto: (state) => {
      //
      return (id) => {
        const user = state.otherUsers.find((i) => i.id === id);
        if (user) {
          return user.photo;
        }
        return "-";
      };
    },
  },
  actions: {
    extractAuthPayload(responseOrPayload) {
      const data = responseOrPayload?.data ?? responseOrPayload;
      if (!data) return null;
      if (data.user || data.requires_verification) return data;
      if (data.data && (data.data.user || data.data.requires_verification))
        return data.data;
      return data;
    },
    async resendCode(key, source) {
      try {
        const verify = await axios.post(
          `${this.authUrl}/api/device/resend-code/${key}`,
          { source }
        );

        if (verify.data) {
          return verify.data;
        }
        return null;
      } catch (error) {
        console.error("Error resending code:", error);
        throw error;
      }
    },
    async verifyCode(key, otp) {
      try {
        const verify = await axios.post(
          `${this.authUrl}/api/elearning/verify-code/${key}`,
          { otp },
          {
            headers: {
              Authorization: `${process.env.VUE_APP_TOKEN}`,
            },
          }
        );
        if (verify.data) {
          const payload = this.extractAuthPayload(verify);
          await this.getToken(payload);
          return true;
        }
        return null;
      } catch (error) {
        console.error("Error resending code:", error);
        throw error;
      }
    },
    async login(name, password) {
      this.sharedStore.startRequest();
      try {
        const keygen = localStorage.getItem("keygen") || "";
        const loginResponse = await axios
          .post(
            `${this.authUrl}/api/elearning/login`,
            {
              name,
              password,
            },
            {
              headers: {
                Authorization: `${process.env.VUE_APP_TOKEN}`,
                "X-Device-Key": keygen,
              },
            }
          )
          .catch((err) => {
            console.log(err?.response?.data?.error);
            Swal.fire({
              title: "Error",
              text: err?.response?.data?.error ?? "Login gagal",
              icon: "error",
            });
            return;
          });
        if (loginResponse) {
          const payload = this.extractAuthPayload(loginResponse);
          if (payload?.requires_verification) {
            console.log("test");
            this.verify_key = payload?.key ?? null;
            router.push(`/verify-code/${this.verify_key}`);
            return;
          }
          await this.getToken(payload ?? loginResponse);
        }
      } catch (err) {
        console.log(err);
      } finally {
        this.sharedStore.endRequest(); // Selalu dijalankan apapun hasilnya
      }
    },
    async getToken(login) {
      const payload = this.extractAuthPayload(login);
      const user = payload?.user;
      const role = payload?.role ?? user?.role ?? "";
      if (!user?.id) {
        Swal.fire({
          title: "Error",
          text: "Data user tidak ditemukan dari response login. Silakan coba login ulang.",
          icon: "error",
        });
        return null;
      }
      const getToken = await axios
        .post(
          `${process.env.VUE_APP_BE_URL}/api/users/token/`,
          {
            userID: user.id,
          },
          {
            headers: {
              "X-External-Token": `${process.env.VUE_APP_TOKEN}`,
            },
          }
        )
        .catch((err) => {
          console.log(err?.response?.data?.error);
          Swal.fire({
            title: "Error",
            text: err?.response?.data?.detail ?? "Gagal mengambil token",
            icon: "error",
          });
          return;
        });
      if (getToken) {
        const localUser = Object.assign(
          {},
          {
            id_key: user.id_key,
            updated_at: new Date(),
            id: user.id,
          }
        );

        localStorage.setItem("user", JSON.stringify(localUser));
        localStorage.setItem("token", getToken.data.access);
        localStorage.setItem("keygen", user.keygen_elearning);
        localStorage.setItem("refresh", getToken.data.refresh);
        localStorage.setItem(
          "token_expired_at",
          getToken.data.access_expires_at
        );
        this.currentUser = await user;

        this.role = await role;

        await new Promise((resolve) => setTimeout(resolve, 2000));

        if (this.role === "teacher") {
          router.push("/instructor/instructor-dashboard");
          return;
        } else if (this.role === "student") {
          router.push("/students/student-dashboard");
          return;
        } else if (this.role === "principal") {
          router.push("/principal/dashboard");
          return;
        }
      }
    },
    logout(redirectToLogin = true) {
      window.localStorage.removeItem("user");
      window.localStorage.removeItem("token");
      window.localStorage.removeItem("token_expired_at");
      window.localStorage.removeItem("refresh");
      this.currentUser = null;
      this.role = null;
      if (redirectToLogin && router?.currentRoute?.value?.path !== "/login") {
        router.push("/login");
      }
    },

    async authUser() {
      this.sharedStore.startRequest();

      try {
        const id_key = JSON.parse(localStorage.getItem("user") || "{}").id_key;
        const keygen = localStorage.getItem("keygen");
        const response = await axios.get(
          `${this.authUrl}/api/elearning/auth-user`,
          {
            params: {
              keygen: keygen,
              id_key: id_key,
            },
          }
        );
        this.currentUser = response.data.user;
        this.otherUsers = response.data.all_user || [];
        this.role = response.data.role;
      } catch (error) {
        console.log(error);
      } finally {
        this.sharedStore.endRequest();
      }
    },
  },
});
